import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress, Alert, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import { format } from 'date-fns';
import { PaymentReceived } from '../types/accounting';
import { accountingService } from '../services/accountingService';
import { projectService } from '../services/projectService';
import { Project } from '../types/project';
import AddPaymentModal from '../components/AddPaymentModal';
import { User } from '../types/user';
import { userService } from '../services/userService';

const ReceivedPage: React.FC = () => {
  const [payments, setPayments] = useState<PaymentReceived[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  // State for filters
  const [projectFilter, setProjectFilter] = useState('');
  const [projects, setProjects] = useState<Project[]>([]);
  const [receivedByFilter, setReceivedByFilter] = useState('');
  const [users, setUsers] = useState<User[]>([]);

  const fetchPayments = async (params: Parameters<typeof accountingService.getPayments>[0]) => {
    try {
      setLoading(true);
      setError(null);
      const data = await accountingService.getPayments(params);
      setPayments(data);
    } catch (err) {
      setError('Failed to fetch payments.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchFilterData = async () => {
      try {
        const projectData = await projectService.getProjects();
        setProjects(projectData);
        const userData = await userService.getUsers();
        setUsers(userData);
      } catch (err) {
        console.error("Failed to fetch filter data", err);
      }
    };
    fetchFilterData();
  }, []);

  useEffect(() => {
    fetchPayments({ project: projectFilter, received_by: receivedByFilter });
  }, [projectFilter, receivedByFilter]);

  const handleSave = () => {
    fetchPayments({ project: projectFilter, received_by: receivedByFilter });
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">Payments Received</Typography>
        <Button variant="contained" onClick={() => setIsAddModalOpen(true)}>Add Payment</Button>
      </Box>

      <Box sx={{ mb: 2, display: 'flex', gap: 2, width: { xs: '100%', sm: '75%' } }}>
        <FormControl fullWidth>
          <InputLabel>Filter by Project</InputLabel>
          <Select value={projectFilter} label="Filter by Project" onChange={(e) => setProjectFilter(e.target.value)}>
            <MenuItem value=""><em>All Projects</em></MenuItem>
            {projects.map(p => (
              <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth>
          <InputLabel>Filter by Received By</InputLabel>
          <Select value={receivedByFilter} label="Filter by Received By" onChange={(e) => setReceivedByFilter(e.target.value)}>
            <MenuItem value=""><em>All Users</em></MenuItem>
            {users.map(u => (
              <MenuItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Project</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Received By</TableCell>
              <TableCell align="right">Amount</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {payments.map((payment) => (
              <TableRow key={payment.id}>
                <TableCell>{payment.date_received && !isNaN(new Date(payment.date_received).getTime()) ? format(new Date(payment.date_received), 'MMM dd, yyyy') : 'N/A'}</TableCell>
                <TableCell>{payment.project_details?.name || 'N/A'}</TableCell>
                <TableCell>{payment.description}</TableCell>
                <TableCell>{payment.received_by_details?.first_name || 'N/A'} {payment.received_by_details?.last_name || ''}</TableCell>
                <TableCell align="right">${parseFloat(payment.amount).toFixed(2)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <AddPaymentModal open={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} onSave={handleSave} />
    </Box>
  );
};

export default ReceivedPage;